/**
 * useUserManagement Hook - User Management Enterprise
 * Hook enterprise per gestione completa utenti con ruoli, MFA e audit
 */

import { useState, useEffect, useCallback } from "react";
import { API_ENDPOINTS, apiFetchJson } from "@/lib/api";

export type UserRole = "SUPER_ADMIN" | "ADMIN" | "MARRANI" | "TRIAL";

export interface User {
    id: number;
    username: string;
    email?: string;
    discord_id?: string;
    discord_username?: string;
    discord_avatar?: string;
    role: UserRole;
    is_active: boolean;
    created_at: string;
    last_login?: string;
    mfa_enabled?: boolean;
    trial_expires_at?: string;
}

export interface UserStats {
    total_users: number;
    active_users: number;
    new_users_today: number;
    new_users_week: number;
    by_role: Record<UserRole, number>;
    retention_rate: number;
}

export interface UserSession {
    id: string;
    user_id: number;
    ip_address: string;
    user_agent: string;
    created_at: string;
    last_activity: string;
    is_current: boolean;
}

export interface AuditLog {
    id: number;
    user_id: number;
    username: string;
    action: string;
    details: string;
    ip_address: string;
    timestamp: string;
}

export interface UserFilters {
    role?: UserRole;
    search?: string;
    active_only?: boolean;
    mfa_enabled?: boolean;
}

export function useUserManagement() {
    const [users, setUsers] = useState<User[]>([]);
    const [stats, setStats] = useState<UserStats | null>(null);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [userSessions, setUserSessions] = useState<UserSession[]>([]);
    const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
    const [filters, setFilters] = useState<UserFilters>({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchUsers = useCallback(async (currentFilters: UserFilters = {}) => {
        try {
            setError(null);
            const data = await apiFetchJson<any>(API_ENDPOINTS.ADMIN_USERS, {
                query: {
                    role: currentFilters.role,
                    search: currentFilters.search,
                    active_only: currentFilters.active_only ? "true" : undefined,
                },
            });
            setUsers(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Error fetching users:", err);
            setError(err instanceof Error ? err.message : "Unknown error");
            setUsers([]);
        }
    }, []);

    const fetchUserStats = useCallback(async () => {
        try {
            const data = await apiFetchJson<any>(API_ENDPOINTS.ADMIN_USER_STATS);
            setStats(data || null);
        } catch (err) {
            console.error("Error fetching user stats:", err);
            setStats(null);
            setError(err instanceof Error ? err.message : "Unknown error");
        }
    }, [users.length]);

    const fetchUserSessions = useCallback(async (userId: number) => {
        try {
            const data = await apiFetchJson<any>(API_ENDPOINTS.ADMIN_USER_SESSIONS(userId));
            setUserSessions(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Error fetching user sessions:", err);
            setUserSessions([]);
            setError(err instanceof Error ? err.message : "Unknown error");
        }
    }, []);

    const fetchAuditLogs = useCallback(async (userId?: number) => {
        try {
            const data = await apiFetchJson<any>(API_ENDPOINTS.ADMIN_AUDIT_LOGS, {
                query: { user_id: userId },
            });
            setAuditLogs(Array.isArray(data?.logs) ? data.logs : []);
        } catch (err) {
            console.error("Error fetching audit logs:", err);
            setAuditLogs([]);
            setError(err instanceof Error ? err.message : "Unknown error");
        }
    }, []);

    const updateUserRole = useCallback(
        async (userId: number, newRole: UserRole) => {
            try {
                await apiFetchJson<any>(API_ENDPOINTS.ADMIN_USER_ROLE(userId), {
                    method: "PUT",
                    json: { new_role: newRole },
                });

                setUsers((prev) =>
                    prev.map((u) => (u.id === userId ? { ...u, role: newRole } : u)),
                );
                await fetchAuditLogs();
                return { success: true };
            } catch (err) {
                return {
                    success: false,
                    error: err instanceof Error ? err.message : "Unknown error",
                };
            }
        },
        [fetchAuditLogs],
    );

    const toggleUserStatus = useCallback(async (userId: number) => {
        try {
            await apiFetchJson<any>(API_ENDPOINTS.ADMIN_USER_STATUS(userId), {
                method: "PUT",
            });

            setUsers((prev) =>
                prev.map((u) => (u.id === userId ? { ...u, is_active: !u.is_active } : u)),
            );
            return { success: true };
        } catch (err) {
            return {
                success: false,
                error: err instanceof Error ? err.message : "Unknown error",
            };
        }
    }, []);

    const applyFilters = useCallback(
        (newFilters: UserFilters) => {
            setFilters(newFilters);
            fetchUsers(newFilters);
        },
        [fetchUsers],
    );

    const selectUser = useCallback(
        (user: User) => {
            setSelectedUser(user);
            fetchUserSessions(user.id);
            fetchAuditLogs(user.id);
        },
        [fetchUserSessions, fetchAuditLogs],
    );

    const refresh = useCallback(async () => {
        setIsLoading(true);
        await Promise.all([
            fetchUsers(filters),
            fetchUserStats(),
            fetchAuditLogs(),
        ]);
        setIsLoading(false);
    }, [fetchUsers, fetchUserStats, fetchAuditLogs, filters]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    return {
        users,
        stats,
        selectedUser,
        userSessions,
        auditLogs,
        filters,
        isLoading,
        error,
        selectUser,
        applyFilters,
        updateUserRole,
        toggleUserStatus,
        refresh,
    };
}
